import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  MenuItem,
} from "@chakra-ui/react";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { CapitalActivityAdjustmentsModal } from "features/capital-activity/event/adjustments/CapitalActivityAdjustmentsModal";
import { CapitalEventStatsCard } from "features/capital-activity/event/CapitalEventStatsCard";
import { useAdjustmentsModal } from "features/capital-activity/event/useAdjustmentsModal";
import DistributionReadOnlyDrawdownTable from "features/distributions/DistributionReadOnlyDrawdownTable";
import { FundFamilyDistributionBreakdownTable } from "features/distributions/FundFamilyDistributionBreakdownPage";
import {
  getDistributionAdjustmentTableDataRetrieveQueryKey,
  getDistributionBreakdownTableDataRetrieveQueryKey,
  getDistributionUnfundedBreakdownTableDataRetrieveQueryKey,
  useDistributionAdjustmentTableDataRetrieveSuspense,
} from "gen/endpoints/distributions/distributions";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { AdjustableFundFamilySubEventTypeEnum } from "gen/model/adjustableFundFamilySubEventTypeEnum";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { CeleryWorkflowStatus } from "gen/model/celeryWorkflowStatus";
import { FundFamilyEventAPIData } from "gen/model/fundFamilyEventAPIData";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import {
  PageWizardProvider,
  PageWizardRoutes,
} from "shared/components/layout/PageWizard";
import { useRefreshOnSubeventsComplete } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { ErrorIcon } from "shared/icons/ErrorIcon";
import { rootRoute } from "shared/navigation/Routes";
import { getErrorMessageFromCeleryWorkflow } from "shared/utils/ErrorUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const FundFamilyDistributionPage: React.FC = () => {
  const { t } = useTranslation(["common", "distribution"]);
  const navigate = useNavigate();

  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const activityEventPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId }).$;

  const isDraft =
    eventData.state === FundFamilyEventStateMachineFieldsEnum.draft;

  if (!isDraft) {
    return (
      <PageContent isFullHeight={true}>
        <PageHeader
          title={t("distribution:header.title")}
          badge={getCapitalActivityEventStateBadge(eventData.state, t, false)}
          backButton={{
            title: t("common:capitalEvent"),
            path: activityEventPath,
          }}
          stats={
            <CapitalEventStatsCard
              capitalActivityData={eventData}
              isDraftEvent={isDraft}
            />
          }
        />
        <PageBody>
          <Flex direction="column" gap="24px">
            <DistributionReadOnlyDrawdownTable
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
            />
            <FundFamilyDistributionBreakdownTable
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
            />
          </Flex>
        </PageBody>
      </PageContent>
    );
  }

  const navigateToActivityEvent = () => {
    navigate(activityEventPath);
  };

  const hiddenPages: Partial<Record<string, boolean>> = {
    capitalActivityEventDistributionOverview: true,
  };

  return (
    <PageWizardProvider
      finishTitle={t("distribution:footer.finalizeDistribution")}
      onFinish={navigateToActivityEvent}
      onExit={navigateToActivityEvent}
      hiddenPages={hiddenPages}
    >
      {/* NOTE: Can't set isFullHeight={true} because the main table in
          FundFamilyDistributionAllocationPage does not have enough
          space when the Fee Holdback card is visible. */}
      <PageContent>
        <PageWizardRoutes />
      </PageContent>
    </PageWizardProvider>
  );
};

export const FundFamilyDistributionPageTitle: React.FC<{
  title: string;
  eventData: FundFamilyEventAPIData;
  onEdit?: () => void;
  onDelete?: () => void;
  showAdjustments?: boolean;
  backButton?: {
    title: string;
    path?: string;
  };
}> = ({ title, eventData, onEdit, onDelete, showAdjustments, backButton }) => {
  const { t } = useTranslation(["common", "distribution", "capitalActivity"]);

  const { data: tableData, isFetching } =
    useDistributionAdjustmentTableDataRetrieveSuspense(
      eventData.fund_family_id,
      eventData.id!,
      {
        query: {
          refetchOnMount: true,
        },
      },
    );

  const invalidateQueries = useInvalidateQueryKeys();
  const refetchPageData = useCallback(async () => {
    await invalidateQueries(
      getDistributionAdjustmentTableDataRetrieveQueryKey(
        eventData.fund_family_id,
        eventData.id!,
      ),

      getDistributionBreakdownTableDataRetrieveQueryKey(
        eventData.fund_family_id,
        eventData.id!,
      ),

      getDistributionUnfundedBreakdownTableDataRetrieveQueryKey(
        eventData.fund_family_id,
        eventData.id!,
      ),
    );
  }, [eventData.fund_family_id, eventData.id, invalidateQueries]);

  const { areAnyProcessing, cache: workflowStatuses } =
    useRefreshOnSubeventsComplete({
      fundFamilyId: eventData.fund_family_id,
      fundFamilyEventId: eventData.id!,
      refetch: refetchPageData,
    });

  const errorMessages = useMemo(
    () =>
      Array.from(workflowStatuses.values())
        .map((status: CeleryWorkflowStatus) =>
          status.state == CeleryWorkflowStateMachineFieldsEnum.failed
            ? getErrorMessageFromCeleryWorkflow(status)
            : undefined,
        )
        .filter((message) => !!message),
    [workflowStatuses],
  );

  const postSubmit = useCallback(async () => {
    await refetchPageData();
  }, [refetchPageData]);

  const { onSubmit } = useAdjustmentsModal({
    eventData,
    postSubmit,
    subEventTypeBeingAdjusted:
      AdjustableFundFamilySubEventTypeEnum.distribution,
  });

  const {
    value: isAddAdjustmentsModalOpen,
    setTrue: openAddAdjustmentsModal,
    setFalse: closeAddAdjustmentsModal,
  } = useBoolean(false);

  return (
    <>
      {errorMessages.map((message) => (
        <Alert status="error" key={message} mt="8px">
          <AlertIcon>
            <ErrorIcon />
          </AlertIcon>
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      ))}
      <PageHeader
        title={title}
        workflowName={!backButton ? t("common:distribution") : undefined}
        backButton={backButton}
        badge={getCapitalActivityEventStateBadge(eventData.state, t)}
        actions={
          eventData.state === FundFamilyEventStateMachineFieldsEnum.draft &&
          showAdjustments ? (
            <Restricted permission={PermissionTypeEnum.write}>
              <PageMenu>
                <MenuItem
                  disabled={areAnyProcessing}
                  onClick={openAddAdjustmentsModal}
                >
                  {t("capitalActivity:adjustments.title")}
                </MenuItem>
              </PageMenu>
            </Restricted>
          ) : undefined
        }
        stats={
          <CapitalEventStatsCard
            capitalActivityData={eventData}
            isDraftEvent={
              eventData.state === FundFamilyEventStateMachineFieldsEnum.draft
            }
            onEdit={onEdit}
            onDelete={onDelete}
          />
        }
      />
      <CapitalActivityAdjustmentsModal
        onSubmit={onSubmit}
        isLoading={isFetching}
        tableData={tableData}
        isOpen={isAddAdjustmentsModalOpen}
        onClose={closeAddAdjustmentsModal}
      />
    </>
  );
};
